/**
 * projectRights — RP01 / RP02 (revue produit 2026-09, phase R0)
 *
 * Calcule ce que l'écran d'un projet peut PROPOSER à l'utilisateur, à partir
 * des droits réellement accordés par le serveur :
 *   - `role_projet` et `permissions` effectives renvoyés par
 *     GET /api/chantiers/{id}/my-permissions (hook usePermissions) ;
 *   - le rôle global (`user.role`) pour les routes qui le testent encore.
 *
 * Chaque règle recopie le contrôle de la route serveur correspondante (voir
 * commentaires). Aucun droit serveur n'est modifié ici : on cesse seulement
 * d'afficher des boutons qui finissent en refus (403).
 */

// Rôles projet « maîtrise d'œuvre » (backend/routes/permissions_routes.py).
export const MOE_ROLES = ['archi', 'moe_principal', 'moe_membre'];

// Sous-onglets / cartes de « Suivi chantier » masqués au client.
//  - Points ouverts : vocabulaire d'entreprise (verdict fiche 06 §3).
//  - Priorités, carte des éléments : GET /elements refuse le client (403 avalé).
export const CLIENT_HIDDEN_SUIVI_TABS = ['points-ouverts', 'priorites'];

export function isClientUser(userRole, roleProjet) {
  return userRole === 'client' || roleProjet === 'client';
}

/**
 * @param {object} args
 * @param {string} args.userRole   rôle global (user.role)
 * @param {string|null} args.roleProjet rôle projet renvoyé par /my-permissions
 * @param {object} args.perms      permissions effectives renvoyées par /my-permissions
 */
export function projectRights({ userRole, roleProjet, perms } = {}) {
  // Tant que /my-permissions n'a pas répondu (ou en cas d'échec), l'associé et
  // l'architecte gardent leurs boutons : leur rôle global leur donne « archi »
  // sur leurs projets.
  // RP32 — le collaborateur est « moe_membre » (rôle implicite RP33) en attendant la réponse.
  const role = roleProjet || (['admin', 'architect'].includes(userRole) ? 'archi' : (userRole === 'collaborateur' ? 'moe_membre' : null));
  const isMoe = MOE_ROLES.includes(role);
  const p = perms || {};
  return {
    isClient: isClientUser(userRole, roleProjet),
    // PUT /chantiers/{id} (fiche MOA, propriétaires) : rôle global admin/architect.
    canEditChantier: ['admin', 'architect'].includes(userRole),
    // POST/PUT/DELETE /chantiers/{id}/marches, POST /devis/upload : archi, moe_principal, moe_membre.
    canWriteMarches: isMoe,
    // POST /chantiers/{id}/dpgf/import : archi, ou droit « Modifier le DPGF » accordé.
    // (permission effective : vraie par défaut pour « archi », sauf retrait explicite)
    canImportDpgf: roleProjet ? !!p.can_modify_dpgf : role === 'archi',
    // POST/PUT /chantiers/{id}/factures : archi, moe_principal, moe_membre (et entreprise pour
    // ses propres factures, hors de cet écran). DELETE : MOE seulement.
    canWriteFactures: isMoe,
    // GET /chantiers/{id}/factures : MOE + entreprise (client et AMO/consultant refusés).
    canReadFactures: isMoe || role === 'entreprise',
    // POST /meetings : rôle projet MOE (archi, moe_principal, moe_membre) — PLA-02 a
    // retiré le double verrou sur le rôle global.
    canPlanMeeting: isMoe,
  };
}

export default projectRights;
